import React from 'react';
import { AlertTriangle, ExternalLink, Database, Flame, X } from 'lucide-react';

export const DataDisclaimer: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Header — matches AppHeader */}
      <header className="bg-navy-900 border-b border-navy-800 shadow-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors text-sm"
              title="Close this tab"
            >
              <X className="w-4 h-4" />
              <span>Close</span>
            </button>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center shadow-sm">
                <Flame className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-[17px] font-bold text-white font-brand tracking-tight">Data Disclaimer</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">

          {/* Warning Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-lg font-semibold text-amber-800 mb-2">Important Notice</h2>
                <p className="text-amber-700">
                  This disclaimer outlines the terms and limitations regarding the fuel price data provided by FuelHunt.
                  Please read carefully before using our services.
                </p>
              </div>
            </div>
          </div>

          {/* Data Disclaimer Content */}
          <div className="prose prose-gray max-w-none">
            <div className="flex items-center gap-2 mb-6">
              <Database className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-gray-900 m-0">Data Disclaimer</h2>
            </div>

            <div className="space-y-6 text-gray-700">
              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Data Sources</h3>
                <p>
                  Fuel price data displayed by FuelHunt is sourced from publicly available data provided by the UK Government{' '}
                  <a
                    href="https://www.gov.uk/guidance/access-fuel-price-data"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-600 hover:text-amber-700 underline inline-flex items-center gap-1"
                  >
                    (gov.uk/guidance/access-fuel-price-data)
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  {' '}and, where applicable, retailer APIs.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Data Accuracy</h3>
                <p>
                  While we strive to ensure that information is accurate and up to date, FuelHunt provides this data "as is"
                  without any guarantees of completeness, accuracy, or timeliness.
                </p>
                <p className="mt-3">
                  Fuel prices can change frequently throughout the day, and there may be delays between price updates at fuel
                  stations and their reflection in our system. Always verify current prices directly with the fuel station
                  before making purchasing decisions.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Intended Use</h3>
                <p>
                  This service is for informational purposes only and should not be relied upon for financial or operational decisions.
                  Users are responsible for verifying information independently before acting on it.
                </p>
                <p className="mt-3">
                  FuelHunt is designed to help users compare fuel prices and make informed decisions, but should not be the sole
                  basis for important travel or business decisions.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Limitation of Liability</h3>
                <p>
                  FuelHunt and its developers accept no liability for any loss, damage, or inconvenience arising from the use of,
                  or reliance on, this data or website.
                </p>
                <p className="mt-3">
                  This includes, but is not limited to:
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Financial losses due to inaccurate price information</li>
                  <li>Wasted time or fuel due to outdated station information</li>
                  <li>Inconvenience caused by station closures or service interruptions</li>
                  <li>Technical issues affecting the availability or accuracy of data</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Data Updates</h3>
                <p>
                  We make reasonable efforts to update fuel price data regularly, but cannot guarantee the frequency or timing
                  of updates. Some data sources may update more frequently than others.
                </p>
                <p className="mt-3">
                  Station information (location, facilities, opening hours) is updated periodically but may not reflect
                  recent changes. Always verify station details before visiting.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Third-Party Data</h3>
                <p>
                  Where data is sourced from third-party retailers or government databases, FuelHunt acts as an aggregator
                  and cannot guarantee the accuracy of information provided by these external sources.
                </p>
              </section>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                For questions about our data handling or this disclaimer, please contact us at{' '}
                <a href="mailto:fuelhunt@hotmail.com" className="text-amber-600 hover:text-amber-700">
                  fuelhunt@hotmail.com
                </a>
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Last updated: {new Date().toLocaleDateString('en-GB', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DataDisclaimer;
